import React from 'react';
import { useTranslation } from 'react-i18next';
import './ConfirmationStep.scss';
import { CardEvent } from '../../EventCard/EventCard';
import { useMvpPartners } from '../../../hooks/useMvpPartners';

interface ConfirmationStepProps {
  eventData: CardEvent;
  initialData?: Record<string, any>;
  onNext: (data: Record<string, any>) => void;
  variant?: 'event' | 'appointment';
  setStepValidity?: (isValid: boolean) => void;
}

export const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  eventData,
  initialData = {},
  onNext,
  variant,
  setStepValidity,
}) => {
  const { t } = useTranslation(variant === 'event' ? 'events' : 'appointments');
  const { retailerOptions } = useMvpPartners();

  // Helper function to get retailer label from ID
  const getRetailerLabel = (retailerId: string): string => {
    if (!retailerId) return '-';
    const retailer = retailerOptions.find(option => option.value === retailerId);
    return retailer ? retailer.label : retailerId;
  };

  // Set this step as valid by default
  React.useEffect(() => {
    if (setStepValidity) {
      setStepValidity(true);
    }
  }, [setStepValidity]);

  // Create a simple handleSubmit function to pass data to onNext
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext({});
  };

  return (
    <form id="current-step-form" onSubmit={handleSubmit} className="confirmation-step">
      <div className="confirmation-step__content">
        <div className="confirmation-step__section">
          <div className="confirmation-step__field">
            <span className="confirmation-step__label">{t('form.event')}</span>
            <span className="confirmation-step__value confirmation-step__value--truncate">{eventData.title}</span>
          </div>
          <div className="confirmation-step__field">
            <span className="confirmation-step__label">{t('form.location')}</span>
            <span className="confirmation-step__value confirmation-step__value--truncate">{eventData.location}</span>
          </div>
          <div className="confirmation-step__field">
            <span className="confirmation-step__label">{t('form.date')}</span>
            <span className="confirmation-step__value">{initialData.date || eventData.date}</span>
          </div>
          <div className="confirmation-step__field">
            <span className="confirmation-step__label">{t('form.time')}</span>
            <span className="confirmation-step__value">{initialData.time || eventData.time}</span>
          </div>
        </div>

        <div className="confirmation-step__section">
          {(eventData.formType === 'product-consultation' || eventData.formType === 'private-intro-class') ? (
            <>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.clientFirstName')}</span>
                <span className="confirmation-step__value">{initialData.clientFirstName || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.clientLastName')}</span>
                <span className="confirmation-step__value">{initialData.clientLastName || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.clientPhone')}</span>
                <span className="confirmation-step__value">{initialData.clientPhone || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.clientEmail')}</span>
                <span className="confirmation-step__value">{initialData.clientEmail || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.willAttend')}</span>
                <span className="confirmation-step__value">{initialData.willAttend ? t(`form.additionalInfo.${initialData.willAttend}`) : '-'}</span>
              </div>
            </>
          ) : (
            <>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.firstName')}</span>
                <span className="confirmation-step__value">{initialData.firstName || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.lastName')}</span>
                <span className="confirmation-step__value">{initialData.lastName || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.phone')}</span>
                <span className="confirmation-step__value">{initialData.phone || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.email')}</span>
                <span className="confirmation-step__value">{initialData.email || '-'}</span>
              </div>
            </>
          )}
          {/* CEU Event Fields */}
          {eventData.formType === 'ceu' && (
            <>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.company')}</span>
                <span className="confirmation-step__value">{initialData.company || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.jobTitle')}</span>
                <span className="confirmation-step__value">{initialData.jobTitle || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span 
                  className="confirmation-step__label"
                  dangerouslySetInnerHTML={{ 
                    __html: t('form.creditReporting')
                  }}
                />
                <span className="confirmation-step__value">{initialData.creditReporting ? t(`form.${initialData.creditReporting.toLowerCase()}`) : '-'}</span>
              </div>
            </>
          )}

          {/* Public Introduction Class Fields */}
          {eventData.formType === 'event-public-intro-class' && (
            <>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.streetAddress')}</span>
                <span className="confirmation-step__value">{initialData.streetAddress || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.city')}</span>
                <span className="confirmation-step__value">{initialData.city || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.province')}</span>
                <span className="confirmation-step__value">{initialData.province || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.postalCode')}</span>
                <span className="confirmation-step__value">{initialData.postalCode || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.foodAllergies')}</span>
                <span className="confirmation-step__value">{initialData.foodAllergies || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.guestName')}</span>
                <span className="confirmation-step__value">{initialData.guestName || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.productsInterested')}</span>
                <span className="confirmation-step__value">{initialData.productsInterested || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.retailer')}</span>
                <span className="confirmation-step__value">{getRetailerLabel(initialData.retailer)}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.tradeProfessionals')}</span>
                <span className="confirmation-step__value">{initialData.tradeProfessionals || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.additionalComments')}</span>
                <span className="confirmation-step__value confirmation-step__value--multiline">{initialData.additionalComments || '-'}</span>
              </div>
            </>
          )}

          {/* Public Ownership Experience Fields */}
          {eventData.formType === 'public-ownership-experience' && (
            <>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.streetAddress')}</span>
                <span className="confirmation-step__value">{initialData.streetAddress || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.city')}</span>
                <span className="confirmation-step__value">{initialData.city || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.province')}</span>
                <span className="confirmation-step__value">{initialData.province || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.postalCode')}</span>
                <span className="confirmation-step__value">{initialData.postalCode || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.foodAllergies')}</span>
                <span className="confirmation-step__value">{initialData.foodAllergies || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.guestName')}</span>
                <span className="confirmation-step__value">{initialData.guestName || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.productsOwned')}</span>
                <span className="confirmation-step__value">{initialData.productsOwned || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.additionalComments')}</span>
                <span className="confirmation-step__value confirmation-step__value--multiline">{initialData.additionalComments || '-'}</span>
              </div>
            </>
          )}

          {/* Dealer Training Fields */}
          {eventData.formType === 'dealer-training' && (
            <>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.companyName')}</span>
                <span className="confirmation-step__value">{initialData.companyName || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.location')}</span>
                <span className="confirmation-step__value">{initialData.location || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.currentBrands')}</span>
                <span className="confirmation-step__value">{initialData.currentBrands || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.trainingGoals')}</span>
                <span className="confirmation-step__value">{initialData.trainingGoals || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.previousTraining')}</span>
                <span className="confirmation-step__value">{initialData.previousTraining || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.foodAllergies')}</span>
                <span className="confirmation-step__value">{initialData.foodAllergies || '-'}</span>
              </div>
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.privateSession')}</span>
                <span className="confirmation-step__value">{initialData.privateSession || '-'}</span>
              </div>
            </>
          )}
        </div>

        {variant === 'appointment' && 
         eventData.formType !== 'special-event' && 
         eventData.formType !== 'unknown-appointment' && (
          <div className="confirmation-step__section">
            {/* Product Consultation Fields */}
            {eventData.formType === 'product-consultation' && (
              <>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.streetAddress')}</span>
                  <span className="confirmation-step__value">{initialData.streetAddress || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.city')}</span>
                  <span className="confirmation-step__value">{initialData.city || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.province')}</span>
                  <span className="confirmation-step__value">{initialData.province || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.postalCode')}</span>
                  <span className="confirmation-step__value">{initialData.postalCode || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.productsInterested')}</span>
                  <span className="confirmation-step__value">{initialData.productsInterested || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.retailer')}</span>
                  <span className="confirmation-step__value">{getRetailerLabel(initialData.retailer)}</span>
                </div>
              </>
            )}

            {/* Private Intro Class Fields */}
            {eventData.formType === 'private-intro-class' && (
              <>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.streetAddress')}</span>
                  <span className="confirmation-step__value">{initialData.streetAddress || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.city')}</span>
                  <span className="confirmation-step__value">{initialData.city || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.province')}</span>
                  <span className="confirmation-step__value">{initialData.province || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.postalCode')}</span>
                  <span className="confirmation-step__value">{initialData.postalCode || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.foodAllergies')}</span>
                  <span className="confirmation-step__value">{initialData.foodAllergies || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.productsInterested')}</span>
                  <span className="confirmation-step__value">{initialData.productsInterested || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.retailer')}</span>
                  <span className="confirmation-step__value">{getRetailerLabel(initialData.retailer)}</span>
                </div>
              </>
            )}

            {/* Private Ownership Experience Fields */}
            {eventData.formType === 'private-ownership' && (
              <>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.streetAddress')}</span>
                  <span className="confirmation-step__value">{initialData.streetAddress || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.city')}</span>
                  <span className="confirmation-step__value">{initialData.city || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.province')}</span>
                  <span className="confirmation-step__value">{initialData.province || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.postalCode')}</span>
                  <span className="confirmation-step__value">{initialData.postalCode || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.foodAllergies')}</span>
                  <span className="confirmation-step__value">{initialData.foodAllergies || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.guestName')}</span>
                  <span className="confirmation-step__value">{initialData.guestName || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.productsOwned')}</span>
                  <span className="confirmation-step__value">{initialData.productsOwned || '-'}</span>
                </div>
              </>
            )}

            {/* Boardroom Meeting Fields */}
            {eventData.formType === 'boardroom-meeting' && (
              <>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.meetingDetails')}</span>
                  <span className="confirmation-step__value">{initialData.meetingDetails || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.audioVideoRequirements')}</span>
                  <span className="confirmation-step__value">{initialData.audioVideoRequirements || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.cateringRequirements')}</span>
                  <span className="confirmation-step__value">{initialData.cateringRequirements || '-'}</span>
                </div>
              </>
            )}

            {/* Builder Package Viewing Fields */}
            {eventData.formType === 'builder-package-viewing' && (
              <>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.developer')}</span>
                  <span className="confirmation-step__value">{initialData.developer || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.siteName')}</span>
                  <span className="confirmation-step__value">{initialData.siteName || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.lotSuiteNumber')}</span>
                  <span className="confirmation-step__value">{initialData.lotSuiteNumber || '-'}</span>
                </div>
                <div className="confirmation-step__field">
                  <span className="confirmation-step__label">{t('form.productsInterested')}</span>
                  <span className="confirmation-step__value">{initialData.productsInterested || '-'}</span>
                </div>
              </>
            )}

            {/* Common Additional Comments Field - Only show for known appointment types */}
            {eventData.formType !== 'special-event' && eventData.formType !== 'unknown-appointment' && (
              <div className="confirmation-step__field">
                <span className="confirmation-step__label">{t('form.additionalComments')}</span>
                <span className="confirmation-step__value confirmation-step__value--multiline">{initialData.additionalComments || '-'}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </form>
  );
};
